import { T } from "@repo/transifex";
import { Button } from "~/components";
import type { ScaleBoundUnit, ScaleModel } from "~/types/hardware";

const SCALE_UNITS: readonly ScaleBoundUnit[] = ["g", "kg", "lb", "oz"];

type ScalePairingFormProps = {
  model: ScaleModel | null;
  unit: ScaleBoundUnit;
  onModelChange: (model: ScaleModel) => void;
  onUnitChange: (unit: ScaleBoundUnit) => void;
  onConfigurePrecision: () => void;
  onConnect: () => void;
};

/** Step 1 of pairing: which scale it is and what it weighs in. */
export const ScalePairingForm = ({
  model,
  unit,
  onModelChange,
  onUnitChange,
  onConfigurePrecision,
  onConnect
}: ScalePairingFormProps) => (
  <div className="flex w-full flex-col gap-6 text-center">
    <div className="flex flex-col gap-1">
      <p className="text-xs font-bold uppercase tracking-wide text-text-secondary">
        <T _str="Step 1 of 2" />
      </p>
      <p className="text-2xl font-bold">
        <T _str="Please choose your scale model" />
      </p>
    </div>

    <div className="flex w-full flex-wrap gap-4">
      <Button
        className="min-w-[150px] flex-1"
        onClick={() => {
          onModelChange("pd2");
        }}
        variant={model === "pd2" ? "primary" : "secondary"}
      >
        CAS PD-II
      </Button>

      <Button
        className="min-w-[150px] flex-1"
        onClick={() => {
          onModelChange("pdn");
        }}
        variant={model === "pdn" ? "primary" : "secondary"}
      >
        CAS PDN
      </Button>
    </div>

    <p className="text-left text-lg font-bold">
      <T _str="What does the scale weigh in?" />
    </p>

    <div className="flex w-full flex-wrap gap-4">
      {SCALE_UNITS.map((option) => (
        <Button
          className="min-w-[100px] flex-1"
          key={option}
          onClick={() => {
            onUnitChange(option);
          }}
          variant={unit === option ? "primary" : "secondary"}
        >
          {option}
        </Button>
      ))}
    </div>

    <hr className="border-gray-200" />

    <div className="flex flex-col gap-2">
      <div className="flex w-full gap-4">
        <Button
          className="flex-1"
          disabled={!model}
          onClick={onConfigurePrecision}
          variant="secondary"
        >
          <T _str="Configure precision" />
        </Button>

        <Button
          className="flex-[1.4]"
          disabled={!model}
          onClick={onConnect}
          variant="primary"
        >
          <T _str="Connect" />
        </Button>
      </div>

      <p className="text-sm text-text-secondary">
        <T _str="Configure precision checks a live reading against the scale before you connect. Connect uses the current precision." />
      </p>
    </div>
  </div>
);
